export type NumerologyPackage = "pin" | "isim" | "normal" | "premium";

export type NumerologyInput = {
  package?: NumerologyPackage;
  firstName: string;
  lastName: string;
  day?: number;
  month?: number;
  year?: number;
};

export type NameEquivalent = { char: string; value: number };

export type NumerologyAnalysis = {
  package: NumerologyPackage;
  person: {
    firstName: string;
    lastName: string;
    fullName: string;
    birthDate?: {
      day: number;
      month: number;
      year: number;
    };
  };
  calculations: {
    hasBirthDate: boolean;
    destinyPath?: string;
    destinyReduced?: number;
    color?: string;
    element?: string;
    birthDayNumber?: number;
    expressionNumber: number;
    heartDesireNumber: number;
    personalityNumber: number;
    karmicDebtNumbers: number[];
    pinCodes?: number[];
    yearForesight?: string;
  };
  nameEquivalents: NameEquivalent[];
  context: Record<string, string>;
};

const PACKAGE_VALUES: readonly NumerologyPackage[] = [
  "pin",
  "isim",
  "normal",
  "premium",
];

export function normalizePackage(value: unknown): NumerologyPackage {
  if (typeof value !== "string") return "normal";
  const normalized = value.trim().toLowerCase();
  return (PACKAGE_VALUES as readonly string[]).includes(normalized)
    ? (normalized as NumerologyPackage)
    : "normal";
}

function splitUnicode(str: string): string[] {
  return Array.from(str);
}

function digitsOf(n: number): number[] {
  const str = String(Math.abs(n));
  const result: number[] = [];
  for (const ch of str) {
    const digit = ch.charCodeAt(0) - 48;
    if (digit >= 0 && digit <= 9) result.push(digit);
  }
  return result;
}

function sumDigits(n: number): number {
  return digitsOf(n).reduce((acc, d) => acc + d, 0);
}

function reduceToSingleDigit(n: number): number {
  let total = Math.abs(n);
  while (String(total).length > 1) {
    total = sumDigits(total);
  }
  return total;
}

export function calculateDestinyPath(
  day: number,
  month: number,
  year: number,
): string {
  let total = sumDigits(day) + sumDigits(month) + sumDigits(year);

  const path: number[] = [total];
  while (String(total).length > 1) {
    total = sumDigits(total);
    path.push(total);
  }

  return path.join(">");
}

export function getColor(destinyReduced: number): string {
  switch (destinyReduced) {
    case 1:
      return "Kırmızı";
    case 2:
      return "Turuncu";
    case 3:
      return "Sarı";
    case 4:
      return "Yeşil";
    case 5:
      return "Mavi";
    case 6:
      return "İndigo";
    case 7:
      return "Mor";
    case 8:
      return "Pembe";
    case 9:
      return "Altın";
    default:
      return "";
  }
}

export function getElement(birthYear: number, now: Date = new Date()): string {
  const currentYear = now.getFullYear();
  const difference = currentYear - birthYear;

  if (difference < 10) return "Hava";
  if (difference < 19) return "Su";
  if (difference < 28) return "Ateş";
  if (difference < 37) return "Toprak";
  if (difference < 46) return "Hava";
  if (difference < 55) return "Ateş";
  if (difference < 64) return "Su";
  if (difference < 73) return "Toprak";
  return "";
}

function charToValue(ch: string): number {
  if (["a", "j", "s", "ş"].includes(ch)) return 1;
  if (["b", "k", "t"].includes(ch)) return 2;
  if (["c", "ç", "l", "u", "ü"].includes(ch)) return 3;
  if (["d", "m", "v"].includes(ch)) return 4;
  if (["e", "n", "w"].includes(ch)) return 5;
  if (["f", "o", "ö", "x"].includes(ch)) return 6;
  if (["g", "ğ", "p", "y"].includes(ch)) return 7;
  if (["h", "z", "q"].includes(ch)) return 8;
  if (["ı", "i", "r"].includes(ch)) return 9;
  return 0;
}

export function getNameEquivalents(firstName: string, lastName: string) {
  const chars = splitUnicode(`${firstName}${lastName}`);
  return chars.map((char) => {
    const normalized = char.toLocaleLowerCase("tr-TR");
    return { char, value: charToValue(normalized) };
  });
}

export function calculateExpressionNumber(firstName: string, lastName: string) {
  const chars = splitUnicode(`${firstName}${lastName}`);
  let total = 0;
  for (const ch of chars) {
    total += charToValue(ch.toLocaleLowerCase("tr-TR"));
  }
  return reduceToSingleDigit(total);
}

export function calculateHeartDesireNumber(
  firstName: string,
  lastName: string,
) {
  const chars = splitUnicode(`${firstName}${lastName}`);
  let total = 0;

  for (const raw of chars) {
    const ch = raw.toLocaleLowerCase("tr-TR");
    if (ch === "a") total += 1;
    else if (ch === "u" || ch === "ü") total += 3;
    else if (ch === "e") total += 5;
    else if (ch === "o" || ch === "ö") total += 6;
    else if (ch === "ı" || ch === "i") total += 9;
  }

  return reduceToSingleDigit(total);
}

export function calculatePersonalityNumber(
  firstName: string,
  lastName: string,
) {
  const chars = splitUnicode(`${firstName}${lastName}`);
  let total = 0;

  for (const raw of chars) {
    const ch = raw.toLocaleLowerCase("tr-TR");
    if (["j", "s", "ş"].includes(ch)) total += 1;
    else if (["b", "k", "t"].includes(ch)) total += 2;
    else if (["c", "ç", "l"].includes(ch)) total += 3;
    else if (["d", "m", "v"].includes(ch)) total += 4;
    else if (["n", "w"].includes(ch)) total += 5;
    else if (["f", "x"].includes(ch)) total += 6;
    else if (["g", "ğ", "p", "y"].includes(ch)) total += 7;
    else if (["h", "z", "q"].includes(ch)) total += 8;
    else if (["r"].includes(ch)) total += 9;
  }

  return reduceToSingleDigit(total);
}

export function calculatePinCodes(day: number, month: number, year: number) {
  const a1 = reduceToSingleDigit(day);
  const a2 = reduceToSingleDigit(month);
  const a3 = reduceToSingleDigit(year);
  const a4 = reduceToSingleDigit(a1 + a2 + a3);
  const a5 = reduceToSingleDigit(a1 + a4);
  const b1 = reduceToSingleDigit(a1 + a2);
  const b2 = reduceToSingleDigit(a2 + a3);
  const c1 = reduceToSingleDigit(b1 + b2);
  const pinCode = reduceToSingleDigit(a1 + a2 + a3 + a4 + a5 + b1 + b2 + c1);

  return [a1, a2, a3, a4, a5, b1, b2, c1, pinCode];
}

export function getKarmicDebtNumbers(firstName: string, lastName: string) {
  const counts: Record<number, number> = {
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
    6: 0,
    7: 0,
    8: 0,
    9: 0,
  };

  const chars = splitUnicode(`${firstName}${lastName}`);
  for (const raw of chars) {
    const v = charToValue(raw.toLocaleLowerCase("tr-TR"));
    if (v >= 1 && v <= 9) counts[v] += 1;
  }

  const results: number[] = [];
  for (let i = 1 as const; i <= 9; i++) {
    const count = counts[i];
    if (count === 0 || count > 3) results.push(i);
  }
  return results;
}

export function calculateYearForesight(
  day: number,
  month: number,
  now: Date = new Date(),
): string {
  const year = now.getFullYear();
  let total = sumDigits(day) + sumDigits(month) + sumDigits(year);

  const arr: number[] = [total];
  while (String(total).length > 1) {
    total = sumDigits(total);
    arr.push(total);
  }

  const reversed = arr.slice().reverse();
  const main = reversed[0];
  const master = reversed[1];

  if (master !== undefined && [11, 22, 33].includes(master)) {
    return `${master}>${main}`;
  }

  return String(main);
}

export function buildAnalysis(
  input: NumerologyInput,
  now: Date = new Date(),
): NumerologyAnalysis {
  const pkg = normalizePackage(input.package);

  const firstName = input.firstName.trim();
  const lastName = input.lastName.trim();

  const nameEquivalents = getNameEquivalents(firstName, lastName);

  const expressionNumber = calculateExpressionNumber(firstName, lastName);
  const heartDesireNumber = calculateHeartDesireNumber(firstName, lastName);
  const personalityNumber = calculatePersonalityNumber(firstName, lastName);
  const karmicDebtNumbers = getKarmicDebtNumbers(firstName, lastName);

  const hasBirthDate =
    typeof input.day === "number" &&
    typeof input.month === "number" &&
    typeof input.year === "number" &&
    input.day > 0 &&
    input.month > 0 &&
    input.year > 0;

  let destinyPath: string | undefined;
  let destinyReduced: number | undefined;
  let color: string | undefined;
  let element: string | undefined;
  let birthDayNumber: number | undefined;
  let pinCodes: number[] | undefined;
  let yearForesight: string | undefined;

  if (hasBirthDate) {
    destinyPath = calculateDestinyPath(input.day!, input.month!, input.year!);
    const parts = destinyPath.split(">");
    const last = parts[parts.length - 1];
    destinyReduced = last ? Number.parseInt(last, 10) : undefined;

    if (typeof destinyReduced === "number" && Number.isFinite(destinyReduced)) {
      color = getColor(destinyReduced);
    }

    element = getElement(input.year!, now);
    birthDayNumber = reduceToSingleDigit(input.day!);
    pinCodes = calculatePinCodes(input.day!, input.month!, input.year!);
    yearForesight = calculateYearForesight(input.day!, input.month!, now);
  }

  const fullName = `${firstName} ${lastName}`.trim();

  const birthDate = hasBirthDate
    ? { day: input.day!, month: input.month!, year: input.year! }
    : undefined;

  const context: Record<string, string> = {
    analysis_package: pkg,
    ad: firstName,
    soyad: lastName,
    ad_soyad: fullName,
    email: "",
    gun: hasBirthDate ? String(input.day) : "",
    ay: hasBirthDate ? String(input.month) : "",
    yil: hasBirthDate ? String(input.year) : "",
    dogum_tarihi: hasBirthDate
      ? `${input.day}.${input.month}.${input.year}`
      : "",
    kader_sayisi: destinyPath ?? "",
    kader_sayisi_sade: destinyReduced ? String(destinyReduced) : "",
    renk: color ?? "",
    element: element ?? "",
    dogum_gunu_numarasi: birthDayNumber ? String(birthDayNumber) : "",
    ifade_numarasi: String(expressionNumber),
    kalp_arzusu: String(heartDesireNumber),
    kisilik_numarasi: String(personalityNumber),
    karmik_borc: karmicDebtNumbers.join(","),
    yilin_ongorusu: yearForesight ?? "",
    pin_kodlari: pinCodes ? pinCodes.join(",") : "",
  };

  if (pinCodes) {
    for (let i = 0; i < 9; i++) {
      context[`pin_kodu_${i + 1}`] = String(pinCodes[i] ?? "");
    }
  }

  return {
    package: pkg,
    person: {
      firstName,
      lastName,
      fullName,
      birthDate,
    },
    calculations: {
      hasBirthDate,
      destinyPath,
      destinyReduced,
      color,
      element,
      birthDayNumber,
      expressionNumber,
      heartDesireNumber,
      personalityNumber,
      karmicDebtNumbers,
      pinCodes,
      yearForesight,
    },
    nameEquivalents,
    context,
  };
}

export function renderTemplate(
  template: string,
  context: Record<string, unknown>,
) {
  if (typeof template !== "string" || template === "") return "";

  const normalized: Record<string, string> = {};
  for (const [key, value] of Object.entries(context)) {
    if (value === null || value === undefined) continue;
    if (typeof value === "object") continue;
    normalized[key.toLowerCase()] = String(value);
  }

  if (normalized["ad"] !== undefined) {
    normalized["isim"] = normalized["ad"];
    normalized["name"] = normalized["ad"];
  }

  if (normalized["soyad"] !== undefined) {
    normalized["surname"] = normalized["soyad"];
  }

  if (normalized["ad_soyad"] !== undefined) {
    normalized["isim_soyisim"] = normalized["ad_soyad"];
    normalized["full_name"] = normalized["ad_soyad"];
  }

  return template.replace(
    /\{\{\s*([\p{L}\p{N}_]+)\s*\}\}|\{\s*([\p{L}\p{N}_]+)\s*\}|\[\s*([\p{L}\p{N}_]+)\s*\]/gu,
    (match, g1, g2, g3) => {
      const raw = (g1 || g2 || g3 || "").toString();
      const token = raw.toLowerCase();
      return Object.prototype.hasOwnProperty.call(normalized, token)
        ? normalized[token]
        : match;
    },
  );
}
